import React, { useState, useEffect, useCallback } from 'react'
import { usePage } from '@inertiajs/react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ArrowDownToLine, Sparkles, ArrowUpFromLine, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBrand } from '@/hooks/useBrand'
import type { SyncStatus, FreshnessTier } from '@/types/shared'

type ActivityStatus = 'idle' | 'enqueued' | 'running'

const TIER_ICON_COLORS: Record<FreshnessTier, string> = {
  fresh: 'text-emerald-500',
  stale: 'text-amber-500',
  outdated: 'text-rose-500',
  unknown: 'text-slate-300',
}

const TIER_PULSE_BG: Record<FreshnessTier, string> = {
  fresh: '',
  stale: 'bg-amber-500',
  outdated: 'bg-rose-500',
  unknown: '',
}

export function getRelativeTime(timestamp: string | null): string {
  if (!timestamp) return 'never'
  const diffMs = Date.now() - new Date(timestamp).getTime()
  const totalMinutes = Math.floor(diffMs / 60000)
  if (totalMinutes < 1) return 'just now'
  if (totalMinutes < 60) return `${totalMinutes}m ago`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours < 24) {
    return minutes > 0 ? `${hours}h ${minutes}m ago` : `${hours}h ago`
  }
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours > 0 ? `${days}d ${remHours}h ago` : `${days}d ago`
}

function getAbsoluteTime(timestamp: string | null): string {
  if (!timestamp) return 'Never'
  const date = new Date(timestamp)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (isToday) return `Today at ${time}`
  if (isYesterday) return `Yesterday at ${time}`
  return `${date.toLocaleDateString()} at ${time}`
}

// Map events to pipeline step keys and their effect
const EVENT_MAP: Record<string, { step: string; field?: keyof SyncStatus; status?: ActivityStatus }> = {
  computation_enqueued: { step: 'compute', status: 'enqueued' },
  computation_started: { step: 'compute', status: 'running' },
  rates_computed: { step: 'compute', field: 'lastComputation', status: 'idle' },
  computation_error: { step: 'compute', field: 'lastComputation', status: 'idle' },
  push_enqueued: { step: 'push', status: 'enqueued' },
  push_started: { step: 'push', status: 'running' },
  rates_pushed: { step: 'push', field: 'lastPush', status: 'idle' },
  push_error: { step: 'push', field: 'lastPush', status: 'idle' },
}

const ACTIVITY_LABELS: Record<string, Record<ActivityStatus, string>> = {
  poll: { idle: '', enqueued: 'queued...', running: 'polling...' },
  compute: { idle: '', enqueued: 'queued...', running: 'computing...' },
  push: { idle: '', enqueued: 'queued...', running: 'pushing...' },
}

// Freshness thresholds live server-side in SyncHealth — each step reads its tier from
// syncStatus.health.freshness; the client never computes staleness from timestamps.
const PIPELINE_STEPS = [
  {
    key: 'poll',
    icon: ArrowDownToLine,
    label: 'Bookings Poll',
    description: 'Last time booking data was fetched from your PMS',
    field: 'lastBookingsPolledAt' as const,
  },
  {
    key: 'compute',
    icon: Sparkles,
    label: 'Rate Computation',
    description: 'Last time rates were computed by the pricing engine',
    field: 'lastComputation' as const,
  },
  {
    key: 'push',
    icon: ArrowUpFromLine,
    label: 'Rate Push',
    description: 'Last time the push job checked for rates to send to your PMS',
    field: 'lastPush' as const,
  },
] as const

function getPushDetail(count: number | null | undefined): string | null {
  if (count === null || count === undefined) return null
  if (count === 0) return 'All rates were in sync — nothing to push'
  return `${count} rate${count === 1 ? '' : 's'} pushed to your PMS`
}

// Whether the Rate Push step should be shown as a paused-mode CHECK rather than a real send.
// A paused (dry-run) property stamps a fictional push that must never wear the push-success
// treatment. True when the property is paused now (dryRun) OR the last push was itself a dry
// run (lastPushDryRun) — the latter also covers the window after Go Live, before the first
// real push overwrites the stamp. Positive-evidence only: a missing dryRun with a null/false
// lastPushDryRun (a genuinely live property, or legacy data) keeps the normal treatment.
export function isPushPaused(
  dryRun: boolean | undefined,
  lastPushDryRun: boolean | null | undefined,
): boolean {
  return dryRun === true || lastPushDryRun === true
}

interface PipelineStatusStripProps {
  syncStatus: SyncStatus | null
}

export default function PipelineStatusStrip({ syncStatus }: PipelineStatusStripProps) {
  // A paused (dry-run) property runs the push pipeline in fictional mode: rates are stamped
  // but nothing is sent to the PMS. Present the push step honestly whenever the property is
  // paused now (dryRun) OR the last recorded push was itself a dry run (lastPushDryRun) —
  // the latter also covers the brief window after Go Live before the first real push lands.
  const { dryRun } = usePage<{ dryRun?: boolean }>().props
  const brand = useBrand()
  const [, setTick] = useState(0)
  const [overrides, setOverrides] = useState<Partial<Record<string, string | number>>>({})
  // A step that just completed in real time is fresh by definition — this is event
  // semantics, not threshold math (thresholds stay server-side in SyncHealth).
  const [tierOverrides, setTierOverrides] = useState<Partial<Record<string, FreshnessTier>>>({})
  const [activity, setActivity] = useState<Record<string, ActivityStatus>>({
    poll: 'idle',
    compute: 'idle',
    push: 'idle',
  })

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  const handleSyncUpdate = useCallback((e: Event) => {
    const { event, at, pushed } = (e as CustomEvent).detail
    const mapping = EVENT_MAP[event]
    if (!mapping) return

    // Update activity status
    if (mapping.status != null) {
      setActivity((prev) => ({ ...prev, [mapping.step]: mapping.status! }))
    }

    // Update timestamp on completion events (and mark the step fresh)
    if (mapping.field && at) {
      setOverrides((prev) => ({ ...prev, [mapping.field!]: at }))
      setTierOverrides((prev) => ({ ...prev, [mapping.step]: 'fresh' }))
    }

    // Track push count from real-time events
    if (event === 'rates_pushed' && typeof pushed === 'number') {
      setOverrides((prev) => ({ ...prev, lastPushCount: pushed }))
    }
  }, [])

  useEffect(() => {
    window.addEventListener('crown:sync-status-updated', handleSyncUpdate)
    return () => window.removeEventListener('crown:sync-status-updated', handleSyncUpdate)
  }, [handleSyncUpdate])

  if (!syncStatus) return null

  return (
    <TooltipProvider>
      <div className="hidden items-center lg:flex">
        {PIPELINE_STEPS.map((step, i) => {
          const timestamp = (overrides[step.field] as string | undefined) ?? (syncStatus[step.field] as string | null)
          const tier: FreshnessTier =
            tierOverrides[step.key] ?? syncStatus.health?.freshness[step.key] ?? 'unknown'
          const stepActivity = activity[step.key]
          const isActive = stepActivity === 'enqueued' || stepActivity === 'running'
          // The push step reads as a "check" (not a real send) while paused: mute it and
          // drop the freshness colour/pulse so it never wears the push-success treatment.
          const isPausedPush = step.key === 'push' && isPushPaused(dryRun, syncStatus.lastPushDryRun)
          const iconColor = isActive
            ? 'text-primary'
            : isPausedPush
              ? 'text-muted-foreground'
              : TIER_ICON_COLORS[tier]
          const shouldPulse = !isActive && !isPausedPush && (tier === 'stale' || tier === 'outdated')
          const Icon = step.icon
          const activityLabel = ACTIVITY_LABELS[step.key][stepActivity]

          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <span className="mx-0.5 text-xs text-border select-none">&middot;</span>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn(
                    'flex items-center gap-1 cursor-pointer rounded-md px-1.5 py-1 transition-colors hover:bg-muted/50',
                    isActive && 'bg-primary/[0.06]',
                  )}>
                    <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                      {shouldPulse && (
                        <span
                          className={cn(
                            'absolute inset-[-2px] animate-pulse rounded-full opacity-20',
                            TIER_PULSE_BG[tier],
                          )}
                        />
                      )}
                      {isActive ? (
                        <Loader2 className="relative h-3.5 w-3.5 animate-spin text-primary" />
                      ) : (
                        <Icon className={cn('relative h-3.5 w-3.5', iconColor)} />
                      )}
                    </span>
                    <span
                      className={cn(
                        'text-xs',
                        isActive ? 'font-medium text-primary' : 'text-muted-foreground',
                      )}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {isActive ? activityLabel : getRelativeTime(timestamp)}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="center" className="max-w-64">
                  <p className="text-xs font-medium">{isPausedPush ? 'Rate Check' : step.label}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {isPausedPush
                      ? `While paused, ${brand} checks your rates but sends nothing to your PMS.`
                      : step.description}
                  </p>
                  {isActive ? (
                    <p className="mt-1 text-[11px] font-medium text-primary">
                      {stepActivity === 'enqueued' ? 'Queued, waiting to start...' : 'In progress...'}
                    </p>
                  ) : (
                    <>
                      <p className="mt-1 text-[11px] font-medium">{getAbsoluteTime(timestamp)}</p>
                      {isPausedPush ? (
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          Rates stay in {brand} until you go live.
                        </p>
                      ) : step.key === 'push' ? (() => {
                        const pushCount = overrides.lastPushCount as number | undefined ?? syncStatus.lastPushCount
                        const detail = getPushDetail(pushCount)
                        return detail ? (
                          <p className="mt-0.5 text-[11px] text-muted-foreground">{detail}</p>
                        ) : null
                      })() : null}
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            </React.Fragment>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
